var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Button } from '../button';
import { Text } from '../text';
import { DatePickerAllQuickSelectPresetsSet, DatePickerQuickSelectPresets, DatePickerQuickSelectRangePresets, } from './datePicker.types';
import { dayjs } from './dayjs';
var displayName = 'DatePickerQuickSelect';
export var DatePickerQuickSelect = function (props) {
    var classes = props.classes, disabled = props.disabled, onSelectedChange = props.onSelectedChange, translate = props.translate;
    var quickSelectLookup = React.useMemo(function () {
        var allowedPresets = null;
        if (typeof props.quickSelect === 'boolean') {
            if (props.quickSelect) {
                if (props.range)
                    allowedPresets = Object.values(DatePickerQuickSelectRangePresets);
                else
                    allowedPresets = Object.values(DatePickerQuickSelectPresets);
            }
        }
        else if (Array.isArray(props.quickSelect))
            allowedPresets = props.quickSelect;
        if (allowedPresets === null || allowedPresets === void 0 ? void 0 : allowedPresets.length) {
            var today_1 = dayjs().startOf('day');
            // @ts-ignore
            return allowedPresets.reduce(function (prev, preset) {
                var _a;
                var getDates;
                switch (preset) {
                    case DatePickerQuickSelectPresets.START_OF_NEXT_MONTH:
                        getDates = function () { return today_1.add(1, 'month').startOf('month').startOf('day').toISOString(); };
                        break;
                    case DatePickerQuickSelectPresets.START_OF_NEXT_QUARTER: {
                        var endOfFirstQuarter_1 = dayjs().set('month', 2).endOf('month'); // JS dates, even in dayjs, are zero-indexed
                        var endOfSecondQuarter_1 = dayjs().set('month', 5).endOf('month');
                        var endOfThirdQuarter_1 = dayjs().set('month', 8).endOf('month');
                        var endOfFourthQuarter_1 = dayjs().endOf('year');
                        getDates = function () {
                            if (today_1.isSameOrBefore(endOfFirstQuarter_1)) {
                                return endOfFirstQuarter_1.add(1, 'month').startOf('month').toISOString();
                            }
                            if (today_1.isSameOrBefore(endOfSecondQuarter_1)) {
                                return endOfSecondQuarter_1.add(1, 'month').startOf('month').toISOString();
                            }
                            if (today_1.isSameOrBefore(endOfThirdQuarter_1)) {
                                return endOfThirdQuarter_1.add(1, 'month').startOf('month').toISOString();
                            }
                            return endOfFourthQuarter_1.add(1, 'month').startOf('month').toISOString();
                        };
                        break;
                    }
                    case DatePickerQuickSelectPresets.START_OF_NEXT_WEEK:
                        getDates = function () { return today_1.add(1, 'week').startOf('week').toISOString(); };
                        break;
                    case DatePickerQuickSelectPresets.START_OF_TWO_WEEKS_FROM_NOW:
                        getDates = function () { return today_1.add(2, 'week').startOf('week').toISOString(); };
                        break;
                    case DatePickerQuickSelectPresets.TODAY:
                        getDates = function () { return today_1.startOf('day').toISOString(); };
                        break;
                    case DatePickerQuickSelectRangePresets.NEXT_NINETY_DAYS:
                        getDates = function () { return [today_1.startOf('day').toISOString(), today_1.add(90, 'day').startOf('day').toISOString()]; };
                        break;
                    case DatePickerQuickSelectRangePresets.NEXT_SIXTY_DAYS:
                        getDates = function () { return [today_1.startOf('day').toISOString(), today_1.add(60, 'day').startOf('day').toISOString()]; };
                        break;
                    case DatePickerQuickSelectRangePresets.NEXT_TWO_WEEKS:
                        getDates = function () { return [today_1.startOf('day').toISOString(), today_1.add(2, 'week').startOf('day').toISOString()]; };
                        break;
                    case DatePickerQuickSelectRangePresets.NEXT_WEEK:
                        getDates = function () { return [
                            today_1.endOf('week').add(1, 'day').startOf('day').toISOString(),
                            today_1.endOf('week').add(1, 'day').endOf('week').startOf('day').toISOString(),
                        ]; };
                        break;
                    case DatePickerQuickSelectRangePresets.THIS_WEEK:
                        getDates = function () { return [
                            today_1.startOf('week').startOf('day').toISOString(),
                            today_1.endOf('week').startOf('day').toISOString(),
                        ]; };
                        break;
                    default:
                        throw new Error("Unable to perform <DatePicker /> quick selected because an invalid preset of \"".concat(preset, "\" was found"));
                }
                return __assign(__assign({}, prev), (_a = {}, _a[preset] = { getDates: getDates }, _a));
            }, {});
        }
        return props.quickSelect;
    }, [props.quickSelect, props.range]);
    var renderedOptions = React.useMemo(function () {
        return Object.entries(quickSelectLookup).map(function (_a) {
            var _b = __read(_a, 2), key = _b[0], _c = _b[1], getDates = _c.getDates, label = _c.label;
            return (_jsx(Button, __assign({ className: classes.quickSelectPreset, disabled: disabled, onClick: function () {
                    onSelectedChange === null || onSelectedChange === void 0 ? void 0 : onSelectedChange(getDates());
                }, variant: "tertiary" }, { children: _jsx(Text, __assign({ component: "span" }, { children: DatePickerAllQuickSelectPresetsSet.has(key)
                        ? translate('datepicker.quickSelectLabel', { quickSelect: key })
                        : label || key })) }), key));
        });
    }, [classes.quickSelectPreset, disabled, onSelectedChange, quickSelectLookup, translate]);
    return (_jsxs("div", __assign({ className: classes.quickSelect }, { children: [_jsx(Text, __assign({ bold: true, level: 2 }, { children: translate('datepicker.quickSelectHeader') })), renderedOptions] })));
};
DatePickerQuickSelect.displayName = displayName;
